import { type ResolvedPos } from "prosemirror-model";
import { type EditorState, Plugin, PluginKey } from "prosemirror-state";
import { dropPoint } from "prosemirror-transform";
import { type EditorView } from "prosemirror-view";

import { config } from "@moment/design-system/config";

import {
	type CellPosition,
	findAncestorCell,
} from "~/components/canvas/easel/prosemirror/helpers/findAncestorCell";

// By default if the cursor is positioned on a cell, we will position
// the drop cursor above the cell, but this can be overridden to drop
// below the target cell with the dropBelow parameter.
export const findDroppableGap = (
	editorView: EditorView,
	$cursor: ResolvedPos,
	dropBelow?: boolean
): { targetIndex: number; ancestorCell?: CellPosition; dropBelow?: boolean } => {
	// The drop cursor is either in a text cell or between cells.
	// If it's in a text cell we can look up the ancestor chain to
	// find the ancestor cell and then insert after that node.

	// Note ancestor text cell is null if we are in between cells
	const ancestorCell = findAncestorCell($cursor);

	if (ancestorCell === undefined) {
		if (!$cursor.nodeBefore) {
			return { targetIndex: 0, ancestorCell: undefined };
		}
		return { targetIndex: $cursor.pos, ancestorCell: undefined };
	} else {
		const { depth: ancestorDepth } = ancestorCell;

		const beforeIndex = $cursor.before(ancestorDepth);
		const afterIndex = $cursor.after(ancestorDepth);

		const beforeCoords = editorView.coordsAtPos(beforeIndex);
		const cursorCoords = editorView.coordsAtPos($cursor.pos);
		const afterCoords = editorView.coordsAtPos(afterIndex);

		const topGap = cursorCoords.top - beforeCoords.bottom;
		const bottomGap = afterCoords.bottom - cursorCoords.bottom;
		let targetIndex = topGap <= bottomGap ? beforeIndex : afterIndex;
		if (dropBelow) {
			targetIndex = afterIndex;
		}
		return { targetIndex, ancestorCell };
	}
};

//
// Mostly a fork of the MIT licensed version of prosemirror-dropcursor here:
// https://github.com/ProseMirror/prosemirror-dropcursor/tree/d1d9797d61af07367e968ea034a0dafeae6a35f9
//

/**
 * A ProseMirror plugin that renders a rectangle cursor where a drop event will "go." For example,
 * if we reorder a cell, the drop cursor will be a horizontal line across the width of the canvas,
 * indicating where the reordered cell will be placed.
 *
 * Nodes may add a `disableDropCursor` property to their spec to control the showing of a drop
 * cursor inside them. This may be a boolean or a function, which will be called with a view and a
 * position, and should return a boolean.
 */
export function dropCursorPlugin(options: DropCursorViewOpts = {}) {
	return new Plugin({
		key: new PluginKey("drop-cursor-plugin"),
		view(editorView) {
			return new DropCursorView(editorView, options);
		},
	});
}

export interface DropCursorViewOpts {
	width?: number;
	color?: string;
	class?: string;
}

const handlers = [
	"dragover",
	"dragend",
	"drop",
	"dragleave",
	"bottommarginenter",
	"bottommarginexit",
] as const;

/**
 * View that handles the lifecycle of a drop cursor.
 *
 * A dropcursor is rendered as an absolute-positioned rectangle that indicates visually where a drop
 * event will land. This node view will handle creating, updating, and deleting the position and
 * dimensions of this rectangle.
 */
class DropCursorView {
	public readonly width: number;
	public readonly color: string;
	public readonly class?: string;
	public cursorPos: number | null;
	public element: HTMLElement | null;
	public timeout: number | null;
	public handlers: { name: string; handler: (e: Event) => void }[];

	constructor(
		public readonly editorView: EditorView,
		options: DropCursorViewOpts
	) {
		this.width = options.width || 2;
		this.color = options.color || "blue";
		this.class = options.class;
		this.cursorPos = null;
		this.element = null;
		this.timeout = null;

		this.handlers = handlers.map((name) => {
			const handler = (e: Event) => {
				this[name](e as DragEvent);
			};
			editorView.dom.addEventListener(name, handler);
			return { name, handler };
		});
	}

	destroy() {
		this.handlers.forEach(({ name, handler }) =>
			this.editorView.dom.removeEventListener(name, handler)
		);
	}

	update(editorView: EditorView, prevState: EditorState) {
		if (this.cursorPos != null && prevState.doc != editorView.state.doc) {
			if (this.cursorPos > editorView.state.doc.content.size) {
				this.setCursor(null);
			} else {
				this.updateOverlay();
			}
		}
	}

	setCursor(pos: number | null, dropBelow?: boolean) {
		if (pos === this.cursorPos) {
			return;
		}

		this.cursorPos = pos;
		if (pos == null) {
			this.element?.parentNode?.removeChild(this.element);
			this.element = null;
		} else {
			this.updateOverlay(dropBelow);
		}
	}

	updateOverlay(dropBelow?: boolean) {
		if (this.cursorPos === null) {
			return;
		}

		const $pos = this.editorView.state.doc.resolve(this.cursorPos);
		let rect;

		//
		// Find the spot in the document a drop event would go, if we released the mouse now.
		//

		const { targetIndex } = findDroppableGap(this.editorView, $pos, dropBelow);
		const resolvedGapPos = this.editorView.state.doc.resolve(targetIndex);

		//
		// Find the position and dimensions of a rectangle at the place the drop event would go.
		//

		const before = resolvedGapPos.nodeBefore;
		const after = resolvedGapPos.nodeAfter;

		if (before || after) {
			const nodeBefore = this.editorView.nodeDOM(
				targetIndex - (before ? before.nodeSize : 0)
			) as HTMLElement;
			const thisNode = this.editorView.nodeDOM(targetIndex) as HTMLElement;

			if (!(nodeBefore instanceof HTMLElement)) {
				return;
			}
			const nodeBeforeRect = nodeBefore.getBoundingClientRect();
			let top = before ? nodeBeforeRect.bottom : nodeBeforeRect.top;
			if (before && after) {
				top = (top + thisNode.getBoundingClientRect().top) / 2;
			}

			rect = {
				left: nodeBeforeRect.left,
				right: nodeBeforeRect.right,
				top: top - this.width / 2,
				bottom: top + this.width / 2,
			};
		}

		if (!rect) {
			const coords = this.editorView.coordsAtPos(this.cursorPos);
			rect = {
				left: coords.left - this.width / 2,
				right: coords.left + this.width / 2,
				top: coords.top,
				bottom: coords.bottom,
			};
		}

		//
		// Create the drop cursor element if it does not exist.
		//

		const parent = (this.editorView.dom as HTMLElement).offsetParent as HTMLElement;
		if (!this.element) {
			this.element = parent.appendChild(document.createElement("div"));
			if (this.class) {
				this.element.className = this.class;
			}

			this.element.style.cssText = `position: absolute; z-index: ${config.theme?.["zIndex"]?.["draggable"]}; pointer-events: none; background-color:${this.color}`;
		}
		let parentLeft, parentTop;
		if (!parent || (parent == document.body && getComputedStyle(parent).position == "static")) {
			parentLeft = -window.scrollX;
			parentTop = -window.scrollY;
		} else {
			const rect = parent.getBoundingClientRect();
			parentLeft = rect.left - parent.scrollLeft;
			parentTop = rect.top - parent.scrollTop;
		}

		//
		// Draw the drop cursor.
		//

		this.element.style.left = rect.left - parentLeft + "px";
		this.element.style.top = rect.top - parentTop + "px";
		this.element.style.width = rect.right - rect.left + "px";
		this.element.style.height = rect.bottom - rect.top + "px";
	}

	scheduleRemoval(timeout: number) {
		if (this.timeout !== null) {
			clearTimeout(this.timeout);
		}

		// NOTE: Double cast here is because the TS compiler is pointed at NodeJS types instead of
		// browser types, for some reason.
		this.timeout = setTimeout(() => this.setCursor(null), timeout) as unknown as number;
	}

	dragover(event: DragEvent) {
		if (!this.editorView.editable) {
			return;
		}

		const pos = this.editorView.posAtCoords({ left: event.clientX, top: event.clientY });

		const node = pos && pos.inside >= 0 && this.editorView.state.doc.nodeAt(pos.inside);
		const disableDropCursor = node && node.type.spec["disableDropCursor"];
		const disabled =
			typeof disableDropCursor == "function"
				? disableDropCursor(this.editorView, pos)
				: disableDropCursor;

		if (pos && !disabled) {
			let target: number | null | undefined = pos.pos;
			if (this.editorView.dragging && this.editorView.dragging.slice) {
				target = dropPoint(
					this.editorView.state.doc,
					target,
					this.editorView.dragging.slice
				);
				if (target == null) {
					return this.setCursor(null);
				}
			}
			this.setCursor(target);
		}
	}

	dragend() {
		this.scheduleRemoval(20);
	}

	drop() {
		this.scheduleRemoval(20);
	}

	// Remove the cursor if we've left the prosemirror editor dom
	// and if it's not the bottom margin.
	dragleave(event: DragEvent) {
		const target = event.relatedTarget as HTMLElement | null;
		if (target?.id === "bottom-margin") {
			return;
		}

		if (
			event.target == this.editorView.dom ||
			!this.editorView.dom.contains(event.relatedTarget as Node)
		) {
			this.setCursor(null);
		}
	}

	bottommarginenter(_event: DragEvent) {
		const node = this.editorView.dom.lastChild;

		if (node) {
			const pos = this.editorView.posAtDOM(node, 0);

			let target: number | null | undefined = pos;
			if (this.editorView.dragging && this.editorView.dragging.slice) {
				target = dropPoint(
					this.editorView.state.doc,
					target,
					this.editorView.dragging.slice
				);
				if (target == null) {
					return this.setCursor(null);
				}
			}
			this.setCursor(target, true);
		}
	}

	bottommarginexit() {
		this.scheduleRemoval(20);
	}
}
